import { type Node } from '@xyflow/react';

import {
  COUNTRY_GROUP_MIN_STACK_SIZE,
  VALUE_CHAIN_LOGICAL_UNIT_SIBLING_PADDING,
  VALUE_CHAIN_MAP,
  VALUE_CHAIN_STACKED_GROUP_REVEAL_COUNT,
  VALUE_CHAIN_UNIT_SIBLING_PADDING,
} from '../../../../../../constants/general';
import {
  BUSINESS_UNIT_TYPES,
  VALUE_CHAIN_MAP_CUSTOM_NODES,
} from '../../../../../../constants/interfaces';
import { getUnitBusinessUnitType, getUnitEstimated, getUnitHeight } from '../../../../../../utils/reactFlowHelpers';

import type ICountryGroupCandidate from '../interfaces/ICountryGroupCandidate';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';
import type IRootUnitMeta from '../interfaces/IRootUnitMeta';

const getUnitVisualHeight = (
  unitData: ICustomUnitNodeData,
  shouldUseTemporaryChildrenHeight = false,
  shouldUseCountryGroupHeader = false,
): number => {
  const childrenHeightSource = shouldUseTemporaryChildrenHeight
    ? unitData.tempTotalChildrenHeight ?? unitData.totalChildrenHeight
    : unitData.totalChildrenHeight;
  const totalChildrenHeightNumber = Number(childrenHeightSource) || 0;
  const isCollapsedUnit = Boolean(unitData.isCollapsed);
  const isCountryGroupLeaderCollapsed = Boolean(
    shouldUseCountryGroupHeader
    && unitData.isCountryGroupLeader
    && unitData.isCountryGroupCollapsed,
  );

  if (isCountryGroupLeaderCollapsed) {
    return VALUE_CHAIN_MAP.STACKED_GROUP_HEADER_HEIGHT;
  }

  if (isCollapsedUnit) {
    const hasLogicalStatus = getUnitBusinessUnitType(unitData) === BUSINESS_UNIT_TYPES.LOGICAL
      || Boolean(getUnitEstimated(unitData));
    const baseHeaderHeight = VALUE_CHAIN_MAP.CORRECTED_PROFIT_CENTER_HEIGHT;
    const statusHeight = hasLogicalStatus ? VALUE_CHAIN_MAP.LOGICAL_STATUS_HEIGHT : 0;
    return baseHeaderHeight + statusHeight;
  }

  const baseHeight = getUnitHeight(
    unitData.type,
    getUnitBusinessUnitType(unitData),
    getUnitEstimated(unitData),
  );

  if (totalChildrenHeightNumber <= 0) {
    return baseHeight;
  }

  return baseHeight
    + VALUE_CHAIN_MAP.UNIT_RECORD_PADDING
    + totalChildrenHeightNumber
    + VALUE_CHAIN_MAP.UNIT_CONTAINER_MARGIN_BOTTOM;
};

export const getCurrentUnitVisualHeight = (unitData: ICustomUnitNodeData): number => (
  getUnitVisualHeight(unitData, true, true)
);

export const getNodeAbsoluteY = (node: Node, nodesById: Map<string, Node>): number => {
  let absoluteY = node.position.y;
  let currentParentId = node.parentId;

  while (currentParentId) {
    const parentNode = nodesById.get(currentParentId);

    if (!parentNode) {
      break;
    }

    absoluteY += parentNode.position.y;
    currentParentId = parentNode.parentId;
  }

  return absoluteY;
};

// A unit is removed from the ReactFlow graph (node.hidden) only for structural reasons:
// collapsed inside a parent unit, or stacked behind a country-group leader. Off-screen
// (viewport) culling is delegated to ReactFlow's onlyRenderVisibleElements, which skips
// rendering distant nodes WITHOUT marking them hidden — so their connections still draw
// even when a card scrolls off-screen.
export const applyUnitHidingToNodes = (prevNodes: Node[]): Node[] => {
  let hasChanges = false;

  const nextNodes = prevNodes.map((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return node;
    }

    const unitData = node.data as unknown as ICustomUnitNodeData;
    const shouldHide = Boolean(unitData.isHiddenByCountryGroup || unitData.isHiddenByParent);

    if (Boolean(node.hidden) === shouldHide) {
      return node;
    }

    hasChanges = true;

    return {
      ...node,
      hidden: shouldHide,
    };
  });

  return hasChanges ? nextNodes : prevNodes;
};

const getSwimlaneNodesMap = (nodes: Node[]): Map<string, Node> => {
  const swimlaneMap = new Map<string, Node>();

  nodes.forEach((node) => {
    if (node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE) {
      swimlaneMap.set(node.id, node);
    }
  });

  return swimlaneMap;
};

const getRootUnitMetas = (
  nodes: Node[],
  swimlaneMap: Map<string, Node>,
  viewportTop?: number,
  viewportBottom?: number,
): IRootUnitMeta[] => {
  const rootUnitMetas: IRootUnitMeta[] = [];

  nodes.forEach((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return;
    }

    const unitData = node.data as unknown as ICustomUnitNodeData;

    if (unitData.draft) {
      return;
    }

    if ((unitData.depth ?? 0) !== 0) {
      return;
    }

    const swimlaneId = String(unitData.swimlaneId || '');
    if (!swimlaneId) {
      return;
    }

    const swimlaneNode = swimlaneMap.get(swimlaneId);
    if (!swimlaneNode) {
      return;
    }

    const baselineLocalY = typeof unitData.originalPositionY === 'number'
      ? unitData.originalPositionY
      : node.position.y;

    const absoluteY = swimlaneNode.position.y + baselineLocalY;
    const unitHeight = getUnitVisualHeight(unitData);
    const top = absoluteY;
    const bottom = absoluteY + unitHeight;
    const isVisible = viewportTop === undefined || viewportBottom === undefined
      ? true
      : bottom >= viewportTop && top <= viewportBottom;

    rootUnitMetas.push({
      node,
      swimlaneId,
      countryCode: unitData.records?.[0]?.addressCountry,
      absoluteY,
      isVisible,
      unitType: String(unitData.type || ''),
    });
  });

  return rootUnitMetas;
};

const groupRootMetasBySwimlane = (rootUnitMetas: IRootUnitMeta[]): Map<string, IRootUnitMeta[]> => {
  const rootBySwimlane = new Map<string, IRootUnitMeta[]>();

  rootUnitMetas.forEach((meta) => {
    const existing = rootBySwimlane.get(meta.swimlaneId) || [];
    existing.push(meta);
    rootBySwimlane.set(meta.swimlaneId, existing);
  });

  return rootBySwimlane;
};

const getCountryGroupCandidates = (
  rootBySwimlane: Map<string, IRootUnitMeta[]>,
): ICountryGroupCandidate[] => {
  const candidates: ICountryGroupCandidate[] = [];

  rootBySwimlane.forEach((metas, swimlaneId) => {
    const sorted = [...metas].sort(
      (firstMeta, secondMeta) => firstMeta.absoluteY - secondMeta.absoluteY,
    );

    let currentSequence: IRootUnitMeta[] = [];
    let groupIndex = 0;

    const flushSequence = () => {
      if (currentSequence.length < COUNTRY_GROUP_MIN_STACK_SIZE) {
        currentSequence = [];
        return;
      }

      const sequenceCountryCode = currentSequence[0].countryCode;
      const sequenceUnitType = currentSequence[0].unitType;
      if (!sequenceCountryCode || !sequenceUnitType) {
        currentSequence = [];
        return;
      }

      const visibleMembersCount = currentSequence.filter((meta) => meta.isVisible).length;
      const totalMembersCount = currentSequence.length;

      if (visibleMembersCount === 0 || totalMembersCount < COUNTRY_GROUP_MIN_STACK_SIZE) {
        currentSequence = [];
        return;
      }

      const candidateId = `${swimlaneId}__${sequenceCountryCode}__${sequenceUnitType}__${groupIndex}`;
      groupIndex += 1;

      candidates.push({
        id: candidateId,
        swimlaneId,
        countryCode: sequenceCountryCode,
        members: [...currentSequence],
        visibleMembersCount,
        totalMembersCount,
        leaderNodeId: currentSequence[0].node.id,
        unitType: sequenceUnitType,
      });

      currentSequence = [];
    };

    sorted.forEach((meta) => {
      if (!meta.countryCode) {
        flushSequence();
        return;
      }

      if (currentSequence.length === 0) {
        currentSequence = [meta];
        return;
      }

      const lastMeta = currentSequence[currentSequence.length - 1];
      if (lastMeta.countryCode === meta.countryCode && lastMeta.unitType === meta.unitType) {
        currentSequence.push(meta);
        return;
      }

      flushSequence();
      currentSequence = [meta];
    });

    flushSequence();
  });

  return candidates;
};

const getCountryGroupLookup = (
  candidates: ICountryGroupCandidate[],
): {
  candidatesById: Map<string, ICountryGroupCandidate>,
  nodeIdToGroupId: Map<string, string>,
} => {
  const candidatesById = new Map<string, ICountryGroupCandidate>();
  const nodeIdToGroupId = new Map<string, string>();

  candidates.forEach((candidate) => {
    candidatesById.set(candidate.id, candidate);
    candidate.members.forEach((meta) => {
      nodeIdToGroupId.set(meta.node.id, candidate.id);
    });
  });

  return {
    candidatesById,
    nodeIdToGroupId,
  };
};

const applyCountryGroupDataToNodes = (
  nodes: Node[],
  candidatesById: Map<string, ICountryGroupCandidate>,
  nodeIdToGroupId: Map<string, string>,
): Node[] => nodes.map((node) => {
  if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
    return node;
  }

  const existingData = node.data as unknown as ICustomUnitNodeData;
  const originalPositionY = existingData.originalPositionY ?? node.position.y;
  const groupId = nodeIdToGroupId.get(node.id);

  if (!groupId) {
    return {
      ...node,
      draggable: true,
      connectable: true,
      position: { ...node.position },
      data: {
        ...existingData,
        originalPositionY: existingData.originalPositionY ?? node.position.y,
        countryGroupId: undefined,
        countryGroupSize: undefined,
        isCountryGroupLeader: false,
        isCountryGroupCollapsed: false,
        isHiddenByCountryGroup: false,
        countryGroupCountryCode: undefined,
      } as unknown as Record<string, unknown>,
    };
  }

  const candidate = candidatesById.get(groupId);
  if (!candidate) {
    return node;
  }

  const isLeader = node.id === candidate.leaderNodeId;

  return {
    ...node,
    draggable: false,
    connectable: !isLeader,
    position: { ...node.position, y: originalPositionY },
    data: {
      ...existingData,
      originalPositionY,
      countryGroupId: candidate.id,
      countryGroupSize: candidate.totalMembersCount,
      isCountryGroupLeader: isLeader,
      isCountryGroupCollapsed: isLeader,
      isHiddenByCountryGroup: !isLeader,
      countryGroupCountryCode: candidate.countryCode,
    } as unknown as Record<string, unknown>,
  };
});

const hideDescendantsOfHiddenGroupMembers = (nodes: Node[]): Node[] => {
  const unitNodesById = new Map<string, Node>();
  nodes.forEach((node) => {
    if (node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      unitNodesById.set(node.id, node);
    }
  });

  return nodes.map((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return node;
    }

    const nodeData = node.data as unknown as ICustomUnitNodeData;
    if (nodeData.isHiddenByCountryGroup) {
      return node;
    }

    let currentParentId = typeof node.parentId === 'string' ? node.parentId : undefined;
    let shouldBeHiddenByGroup = false;

    while (currentParentId) {
      const parentNode = unitNodesById.get(currentParentId);
      if (!parentNode) {
        break;
      }

      const parentData = parentNode.data as unknown as ICustomUnitNodeData;
      if (parentData.isHiddenByCountryGroup) {
        shouldBeHiddenByGroup = true;
        break;
      }

      currentParentId = typeof parentNode.parentId === 'string' ? parentNode.parentId : undefined;
    }

    if (!shouldBeHiddenByGroup) {
      return node;
    }

    return {
      ...node,
      draggable: false,
      data: {
        ...nodeData,
        isHiddenByCountryGroup: true,
      } as unknown as Record<string, unknown>,
    };
  });
};

const groupRootUnitNodesBySwimlane = (nodes: Node[]): Map<string, Node[]> => {
  const swimlaneToNodes = new Map<string, Node[]>();

  nodes.forEach((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return;
    }

    const nodeData = node.data as unknown as ICustomUnitNodeData;
    if (nodeData.depth !== 0) {
      return;
    }

    const swimlaneIdStr = String(nodeData.swimlaneId || '');
    if (!swimlaneIdStr) {
      return;
    }

    const existing = swimlaneToNodes.get(swimlaneIdStr) || [];
    existing.push(node);
    swimlaneToNodes.set(swimlaneIdStr, existing);
  });

  return swimlaneToNodes;
};

const getRootUnitOriginalY = (node: Node): number => {
  const nodeData = node.data as unknown as ICustomUnitNodeData;
  return typeof nodeData.originalPositionY === 'number' ? nodeData.originalPositionY : node.position.y;
};

const getRootUnitSiblingPadding = (unitData: ICustomUnitNodeData): number => (
  getUnitBusinessUnitType(unitData) === BUSINESS_UNIT_TYPES.LOGICAL
    ? VALUE_CHAIN_LOGICAL_UNIT_SIBLING_PADDING
    : VALUE_CHAIN_UNIT_SIBLING_PADDING
);

const getUnitBaseHeight = (unitData: ICustomUnitNodeData): number => getUnitHeight(
  unitData.type,
  getUnitBusinessUnitType(unitData),
  getUnitEstimated(unitData),
);

export const normalizeNestedUnitLayout = (nodes: Node[]): Node[] => {
  const nextNodes = [...nodes];
  const unitNodesByParentId = new Map<string, Node[]>();
  const nodeIndexById = new Map<string, number>();
  let hasChanges = false;

  nextNodes.forEach((node, index) => {
    nodeIndexById.set(node.id, index);

    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT || !node.parentId?.startsWith('unit_')) {
      return;
    }

    const existing = unitNodesByParentId.get(node.parentId) || [];
    existing.push(node);
    unitNodesByParentId.set(node.parentId, existing);
  });

  const getCurrentNode = (nodeId: string): Node | undefined => {
    const nodeIndex = nodeIndexById.get(nodeId);
    return typeof nodeIndex === 'number' ? nextNodes[nodeIndex] : undefined;
  };

  const updateNode = (node: Node): void => {
    const nodeIndex = nodeIndexById.get(node.id);
    if (typeof nodeIndex !== 'number') {
      return;
    }

    nextNodes[nodeIndex] = node;
  };

  const normalizeChildren = (parentNodeId: string): number => {
    const parentNode = getCurrentNode(parentNodeId);
    if (!parentNode || parentNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return 0;
    }

    const parentData = parentNode.data as unknown as ICustomUnitNodeData;
    if (parentData.isCollapsed || (parentData.isCountryGroupLeader && parentData.isCountryGroupCollapsed)) {
      return Number(parentData.totalChildrenHeight) || 0;
    }

    const childNodes = unitNodesByParentId.get(parentNodeId) || [];
    const visibleChildren = childNodes
      .map((childNode) => getCurrentNode(childNode.id) || childNode)
      .filter((childNode) => {
        const childData = childNode.data as unknown as ICustomUnitNodeData;
        return !childData.isHiddenByParent && !childData.isHiddenByCountryGroup;
      })
      .sort((firstNode, secondNode) => {
        const firstData = firstNode.data as unknown as ICustomUnitNodeData;
        const secondData = secondNode.data as unknown as ICustomUnitNodeData;
        const orderDiff = Number(firstData.orderIndex || 0) - Number(secondData.orderIndex || 0);

        if (orderDiff !== 0) {
          return orderDiff;
        }

        return firstNode.position.y - secondNode.position.y;
      });

    let nextChildY = getUnitBaseHeight(parentData) + VALUE_CHAIN_MAP.UNIT_RECORD_PADDING;
    let totalChildrenHeight = 0;

    visibleChildren.forEach((childNode, index) => {
      const childTotalChildrenHeight = normalizeChildren(childNode.id);
      const childData = childNode.data as unknown as ICustomUnitNodeData;
      const nextChildData = childData.totalChildrenHeight === childTotalChildrenHeight
        ? childData
        : {
          ...childData,
          totalChildrenHeight: childTotalChildrenHeight,
        };
      const hasPositionChange = childNode.position.y !== nextChildY;
      const hasTotalChildrenHeightChange = nextChildData !== childData;

      if (hasPositionChange || hasTotalChildrenHeightChange) {
        hasChanges = true;
        updateNode({
          ...childNode,
          position: {
            ...childNode.position,
            y: nextChildY,
          },
          data: nextChildData as unknown as Record<string, unknown>,
        });
      }

      const childHeight = getUnitVisualHeight(nextChildData, true, true);
      const siblingPadding = getRootUnitSiblingPadding(nextChildData);
      totalChildrenHeight += childHeight;

      if (index < visibleChildren.length - 1) {
        totalChildrenHeight += siblingPadding;
      }

      nextChildY += childHeight + siblingPadding;
    });

    return totalChildrenHeight;
  };

  nextNodes.forEach((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT || node.parentId?.startsWith('unit_')) {
      return;
    }

    const totalChildrenHeight = normalizeChildren(node.id);
    const nodeData = (getCurrentNode(node.id)?.data || node.data) as unknown as ICustomUnitNodeData;

    if (nodeData.totalChildrenHeight === totalChildrenHeight) {
      return;
    }

    const currentNode = getCurrentNode(node.id);
    if (!currentNode) {
      return;
    }

    hasChanges = true;
    updateNode({
      ...currentNode,
      data: {
        ...nodeData,
        totalChildrenHeight,
      } as unknown as Record<string, unknown>,
    });
  });

  return hasChanges ? nextNodes : nodes;
};

export const compactStackedGroupSwimlanes = (nodes: Node[]): Node[] => {
  const nextNodes = [...nodes];
  const swimlaneToNodes = groupRootUnitNodesBySwimlane(nextNodes);

  swimlaneToNodes.forEach((nodesInSwimlane) => {
    const sortedNodes = [...nodesInSwimlane].sort(
      (firstNode, secondNode) => getRootUnitOriginalY(firstNode) - getRootUnitOriginalY(secondNode),
    );

    let accumulatedOffset = 0;
    let nextAvailableY: number | undefined;

    sortedNodes.forEach((node, index) => {
      const nodeData = node.data as unknown as ICustomUnitNodeData;

      if (nodeData.isHiddenByCountryGroup) {
        const currentOrigY = getRootUnitOriginalY(node);
        const nextNode = sortedNodes[index + 1];

        if (nextNode) {
          accumulatedOffset += getRootUnitOriginalY(nextNode) - currentOrigY;
          return;
        }

        const totalHeight = getUnitVisualHeight(nodeData);
        accumulatedOffset += totalHeight + getRootUnitSiblingPadding(nodeData);
        return;
      }

      const origY = getRootUnitOriginalY(node);
      const desiredY = accumulatedOffset > 0 ? origY - accumulatedOffset : origY;
      const nextY = typeof nextAvailableY === 'number' ? Math.max(desiredY, nextAvailableY) : desiredY;

      if (accumulatedOffset > 0) {
        const nodeIndex = nextNodes.findIndex((candidate) => candidate.id === node.id);
        if (nodeIndex >= 0) {
          const currentNode = nextNodes[nodeIndex];
          nextNodes[nodeIndex] = {
            ...currentNode,
            position: {
              ...currentNode.position,
              y: nextY,
            },
          };
        }
      } else if (nextY !== node.position.y) {
        const nodeIndex = nextNodes.findIndex((candidate) => candidate.id === node.id);
        if (nodeIndex >= 0) {
          const currentNode = nextNodes[nodeIndex];
          nextNodes[nodeIndex] = {
            ...currentNode,
            position: {
              ...currentNode.position,
              y: nextY,
            },
          };
        }
      }

      const currentHeight = getUnitVisualHeight(nodeData, true, true);
      nextAvailableY = nextY + currentHeight + getRootUnitSiblingPadding(nodeData);

      if (nodeData.isCountryGroupLeader) {
        const leaderFullHeight = getUnitVisualHeight(nodeData);
        const leaderSaving = leaderFullHeight - VALUE_CHAIN_MAP.STACKED_GROUP_HEADER_HEIGHT;

        if (leaderSaving > 0) {
          accumulatedOffset += leaderSaving;
        }
      }
    });
  });

  return nextNodes;
};

const isDescendantOfStackRoot = (nodes: Node[], nodeId: string, ancestorId: string): boolean => {
  const currentNode = nodes.find((candidate) => candidate.id === nodeId);

  if (!currentNode || !currentNode.parentId) {
    return false;
  }

  if (currentNode.parentId === ancestorId) {
    return true;
  }

  if (!currentNode.parentId.startsWith('unit_')) {
    return false;
  }

  return isDescendantOfStackRoot(nodes, currentNode.parentId, ancestorId);
};

const sortStackNodesByOriginalPosition = (groupNodes: Node[]): Node[] => (
  [...groupNodes].sort((firstNode, secondNode) => (
    getRootUnitOriginalY(firstNode) - getRootUnitOriginalY(secondNode)
  ))
);

export const applyCountryGroupingToNodes = (
  prevNodes: Node[],
  viewportTop?: number,
  viewportBottom?: number,
): Node[] => {
  const swimlaneMap = getSwimlaneNodesMap(prevNodes);
  const rootUnitMetas = getRootUnitMetas(prevNodes, swimlaneMap, viewportTop, viewportBottom);
  const rootBySwimlane = groupRootMetasBySwimlane(rootUnitMetas);
  const candidates = getCountryGroupCandidates(rootBySwimlane);
  const { candidatesById, nodeIdToGroupId } = getCountryGroupLookup(candidates);
  const nodesWithGroupData = applyCountryGroupDataToNodes(
    prevNodes,
    candidatesById,
    nodeIdToGroupId,
  );
  const nodesWithHiddenDescendants = hideDescendantsOfHiddenGroupMembers(nodesWithGroupData);

  return compactStackedGroupSwimlanes(nodesWithHiddenDescendants);
};

const resetCountryGroupNode = (
  node: Node,
  nodeData: ICustomUnitNodeData,
): Node => {
  const originalY = typeof nodeData.originalPositionY === 'number'
    ? nodeData.originalPositionY
    : node.position.y;

  return {
    ...node,
    draggable: true,
    connectable: true,
    position: { ...node.position, y: originalY },
    data: {
      ...nodeData,
      countryGroupId: undefined,
      countryGroupSize: undefined,
      isCountryGroupLeader: false,
      isCountryGroupCollapsed: false,
      isHiddenByCountryGroup: false,
      countryGroupCountryCode: undefined,
      countryGroupSavedHeight: undefined,
      countryGroupBottomY: undefined,
      originalPositionY: originalY,
    } as unknown as Record<string, unknown>,
  };
};

export const removeCountryGroupingFromNodes = (nodes: Node[]): Node[] => {
  let hasChanges = false;

  const nextNodes = nodes.map((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return node;
    }

    const nodeData = node.data as unknown as ICustomUnitNodeData;
    const hasCountryGroupState = Boolean(
      nodeData.countryGroupId
      || nodeData.countryGroupSize
      || nodeData.isCountryGroupLeader
      || nodeData.isCountryGroupCollapsed
      || nodeData.isHiddenByCountryGroup
      || nodeData.countryGroupCountryCode
      || nodeData.countryGroupSavedHeight
      || nodeData.countryGroupBottomY,
    );

    if (!hasCountryGroupState) {
      return node;
    }

    hasChanges = true;

    return resetCountryGroupNode(node, nodeData);
  });

  return hasChanges ? nextNodes : nodes;
};

export const removeScenarioViewCountryGroupingFromNodes = (nodes: Node[]): Node[] => {
  let hasChanges = false;

  const nextNodes = nodes.map((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return node;
    }

    const nodeData = node.data as unknown as ICustomUnitNodeData;
    const hasOriginalPositionY = typeof nodeData.originalPositionY === 'number';
    const hasCountryGroupState = Boolean(
      nodeData.countryGroupId
      || nodeData.countryGroupSize
      || nodeData.isCountryGroupLeader
      || nodeData.isCountryGroupCollapsed
      || nodeData.isHiddenByCountryGroup
      || nodeData.countryGroupCountryCode
      || nodeData.countryGroupSavedHeight
      || nodeData.countryGroupBottomY,
    );

    if (!hasOriginalPositionY && !hasCountryGroupState) {
      return node;
    }

    hasChanges = true;

    return resetCountryGroupNode(node, nodeData);
  });

  return hasChanges ? nextNodes : nodes;
};

export const applyScenarioViewCountryGroupingToNodes = (
  nodes: Node[],
  highlightedUnitNodeIds: ReadonlySet<string>,
): Node[] => {
  if (highlightedUnitNodeIds.size === 0) {
    return applyCountryGroupingToNodes(nodes);
  }

  const groupedNodes = applyCountryGroupingToNodes(nodes);
  const unitNodesById = new Map<string, Node>();
  const highlightedGroupIds = new Set<string>();
  const visibleNodeIdsInHighlightedGroups = new Set<string>();

  groupedNodes.forEach((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return;
    }

    unitNodesById.set(node.id, node);
  });

  const getHighlightedNodeGroupId = (node: Node): string | undefined => {
    let currentNode: Node | undefined = node;

    while (currentNode) {
      const currentData = currentNode.data as unknown as ICustomUnitNodeData;

      visibleNodeIdsInHighlightedGroups.add(currentNode.id);

      if (currentData.countryGroupId) {
        return currentData.countryGroupId;
      }

      if (!currentNode.parentId || !currentNode.parentId.startsWith('unit_')) {
        return undefined;
      }

      currentNode = unitNodesById.get(currentNode.parentId);
    }

    return undefined;
  };

  groupedNodes.forEach((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT || !highlightedUnitNodeIds.has(node.id)) {
      return;
    }

    const groupId = getHighlightedNodeGroupId(node);

    if (groupId) {
      highlightedGroupIds.add(groupId);
    }
  });

  if (highlightedGroupIds.size === 0) {
    return groupedNodes;
  }

  const nodesWithScenarioGroups = groupedNodes.map((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return node;
    }

    const nodeData = node.data as unknown as ICustomUnitNodeData;
    const isVisibleDescendantOfHighlightedGroup = nodeData.isHiddenByCountryGroup
      && visibleNodeIdsInHighlightedGroups.has(node.id);

    if (isVisibleDescendantOfHighlightedGroup && !nodeData.countryGroupId) {
      return {
        ...node,
        draggable: true,
        data: {
          ...nodeData,
          isHiddenByCountryGroup: false,
        } as unknown as Record<string, unknown>,
      };
    }

    if (!nodeData.countryGroupId || !highlightedGroupIds.has(nodeData.countryGroupId)) {
      return node;
    }

    if (visibleNodeIdsInHighlightedGroups.has(node.id)) {
      return resetCountryGroupNode(node, nodeData);
    }

    return {
      ...node,
      draggable: false,
      connectable: false,
      data: {
        ...nodeData,
        isHiddenByCountryGroup: true,
      } as unknown as Record<string, unknown>,
    };
  });

  return compactStackedGroupSwimlanes(nodesWithScenarioGroups);
};

export const expandCountryGroupStackNodes = (
  nodes: Node[],
  countryGroupId: string,
): { nodes: Node[], nodeIdsToReveal: string[] } => {
  const groupUnitNodes = nodes.filter(
    (node) => node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT
      && (node.data as unknown as ICustomUnitNodeData).countryGroupId === countryGroupId,
  );

  if (groupUnitNodes.length === 0) {
    return {
      nodes,
      nodeIdsToReveal: [],
    };
  }

  const nodeIdsToReveal = sortStackNodesByOriginalPosition(groupUnitNodes)
    .slice(0, VALUE_CHAIN_STACKED_GROUP_REVEAL_COUNT)
    .map((node) => node.id);
  const groupRootIds = new Set(groupUnitNodes.map((node) => node.id));
  const expandedNodes = nodes.map((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return node;
    }

    const nodeData = node.data as unknown as ICustomUnitNodeData;

    if (nodeData.countryGroupId === countryGroupId) {
      return resetCountryGroupNode(node, nodeData);
    }

    const isDescendantOfAnyGroupedRoot = Array.from(groupRootIds).some(
      (rootId) => isDescendantOfStackRoot(nodes, node.id, rootId),
    );

    if (isDescendantOfAnyGroupedRoot && nodeData.isHiddenByCountryGroup) {
      return {
        ...node,
        draggable: true,
        data: {
          ...nodeData,
          isHiddenByCountryGroup: false,
        } as unknown as Record<string, unknown>,
      };
    }

    return node;
  });

  return {
    nodes: normalizeNestedUnitLayout(compactStackedGroupSwimlanes(expandedNodes)),
    nodeIdsToReveal,
  };
};
